// =========================================================
// * Vue Material Dashboard PRO - v1.5.2
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard-pro
// * Copyright 2022 Creative Tim (https://www.creative-tim.com)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

import Vue from "vue";
import { ValidationObserver, ValidationProvider, extend, configure } from "vee-validate";
import { integer as baseInteger } from "vee-validate/dist/rules";

import * as rules from "vee-validate/dist/rules";
import numeral from "numeral";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { ClientTable, ServerTable } from "vue-tables-2-premium";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import DashboardPlugin from "./material-dashboard";

// import i18n from "@/i18n"; // Import your i18n instance

// Plugins
import App from "./App.vue";
import Chartist from "chartist";

// store setup
import store from "./store";

import i18n from "./i18n";

// router setup
import router from "./routes/routes";

import AuthService from "./services/auth.service";
import TokenService from "./services/token.service";

// plugin setup
Vue.use(DashboardPlugin);
// global library setup
Vue.prototype.$Chartist = Chartist;

Vue.component("ValidationProvider", ValidationProvider);
Vue.component("ValidationObserver", ValidationObserver);
configure({
  defaultMessage: (field, values) => {
    values._field_ = i18n.t(`fields.${field}`);
    return i18n.t(`validation.${values._rule_}`, values);
  },
});
Object.keys(rules).forEach((rule) => {
  extend(rule, rules[rule]);
});
extend("decimal", {
  params: ["places"], // Allow passing the decimal places as a parameter
  validate: (value, { places }) => {
    const regex = new RegExp(`^\\d+(\\.\\d{0,${places}})?$`);
    return regex.test(value);
  },
  // message: "The value must have at most {places} decimal places.",
});
// extend("integer", {
//   ...baseInteger, // Keep the original validation logic
//   message: (field) => i18n.t("validation.integer", { field }),
// });

Vue.filter("formatDate", (value) => {
  const formatStyle = i18n.locale === "de" ? "dd.MM.yyyy" : "MM-dd-yyyy";
  return value && format(parseISO(value), formatStyle);
});
Vue.filter("formatDateTime", (value) => {
  const formatStyle = i18n.locale === "de" ? "dd.MM.yyyy HH:mm" : "MM-dd-yyyy HH:mm";
  return value && format(parseISO(value), formatStyle);
});
Vue.filter("formatDateTimeSeconds", (value) => {
  const formatStyle = i18n.locale === "de" ? "dd.MM.yyyy HH:mm:ss" : "MM-dd-yyyy HH:mm:ss";
  return value && format(parseISO(value), formatStyle);
});
Vue.filter("formatTime", (value) => {
  if (!value) return value;
  const time = format(parseISO(value), "HH:mm");
  return i18n.locale === "de" ? `${time} Uhr` : time;
});
Vue.filter("formatMonthYear", (value) => {
  return value && format(parseISO(value), "MMMM yyyy");
});
Vue.filter("formatDayOfWeek", (value) => {
  const locale = i18n.locale === "de" ? de : undefined;
  return value && format(parseISO(value), "EE", { locale });
});

numeral.register("locale", "de", {
  delimiters: {
    thousands: ".",
    decimal: ",",
  },
  currency: {
    symbol: "$",
  },
});
numeral.locale(i18n.locale);

Vue.filter("formatNumber", (value) => {
  if (!value && value !== 0) return "";
  numeral.locale(i18n.locale);
  return numeral(value).format("0.00");
});
Vue.filter("formatAmount", (value) => {
  if (!value && value !== 0) return "";
  numeral.locale(i18n.locale);
  return numeral(value).format("0,0.00$");
});
Vue.filter("formatNumberGerman", (value, decimals = 2) => {
  if (!value && value !== 0) return "";
  numeral.locale(i18n.locale);
  return numeral(value).format(`0,0.[${"0".repeat(decimals)}]`);
});
Vue.filter("formatUnit", (value, unit = "", decimals = 2, minDecimals = 0) => {
  if (!value && value !== 0) return "";
  numeral.locale(i18n.locale);
  if (!value && !minDecimals) return "";
  if (minDecimals > 0) {
    return numeral(value).format(`0,0.${"0".repeat(minDecimals)}`) + " " + unit;
  }
  return numeral(value).format(`0,0.[${"0".repeat(decimals)}]`) + " " + unit;
});
Vue.filter("formatBytes", (value) => {
  if (!value && value !== 0) return "";
  numeral.locale("de");
  return numeral(value).format("0.0 b");
});
Vue.filter("capitalized", (name) => {
  const capitalizedFirst = name[0].toUpperCase();
  const rest = name.slice(1);

  return capitalizedFirst + rest;
});
Vue.filter("uppercase", (name) => {
  if (!name) return "";

  return name.toUpperCase();
});

Vue.use(ClientTable, {}, false, "bootstrap4");
Vue.use(ServerTable, {}, false, "bootstrap4");
Vue.component("v-select", vSelect);

Vue.mixin({
  mounted() {
    // Ensure we're in a browser and the component has a $el
    if (this.$el && this.$el.querySelectorAll) {
      const buttons = this.$el.querySelectorAll("button");
      buttons.forEach((button) => {
        // Prevent multiple bindings
        if (!button.__preventDoubleClickBound) {
          button.__preventDoubleClickBound = true;

          const delay = 1000; // default delay
          button.addEventListener("click", () => {
            if (!button.disabled) {
              button.disabled = true;
              setTimeout(() => {
                button.disabled = false;
              }, delay);
            }
          });
        }
      });
    }
  },
});

/* eslint-disable no-new */
async function bootstrap() {
  // Vor dem ersten Rendern die Sitzung auffrischen: Rechteaenderungen kommen so mit dem
  // naechsten Seitenaufruf an, ohne Neuanmeldung. Fehler (offline, abgelaufene Sitzung)
  // blockieren den Start nicht - dann gilt die gecachte Sitzung wie bisher.
  if (TokenService.getToken()) {
    try {
      await AuthService.refreshSession();
    } catch (e) {
      // bewusst still: mit gecachter Sitzung starten
    }
  }

  window.VueSingleton = new Vue({
    el: "#app",
    store,
    i18n,
    router,

    render: (h) => h(App),
  });
}
bootstrap();
